import React from 'react';
import { useSearchContext } from '../../../../context/search/SearchContext';
import { useViewsContext } from '../../../../context/view/ViewsContext';
import { GlobalFilter } from './GlobalFilter';
import { GEAR_FIELD_TYPE } from '../../../../global/enums/GearFieldType';
import { GroupedDateGlobalFilter } from './GroupedDateGlobalFilter';
import { useGetAllFields } from '../../../../context/fields/useGetAllFields';
import { useGlobalizationContext } from '../../../../context/globalization/GlobalizationContext';
import { useCurrentUserContext } from '../../../../context/currentUser/CurrentUserContext';
import { useGetView } from '../../../../context/view/useGetView';
import { GearFacetValue } from '../../../../Model/GearFacets';

interface GlobalFiltersProps {
  initialFacets?: Record<string, GearFacetValue[]>;
}

export const GlobalFilters: React.FC<GlobalFiltersProps> = ({ initialFacets }) => {
  const { selectedView } = useViewsContext();
  const { data } = useSearchContext();
  const { selectedLocale } = useGlobalizationContext();
  const { selectedIndex } = useCurrentUserContext();
  const { data: fields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const { data: currentView } = useGetView(selectedView, selectedLocale);

  const getGlobalFieldDisplayName = React.useCallback(
    (name: string): string => {
      const foundField = fields?.find(field => field.name === name);
      if (!foundField) {
        return name;
      }
      return foundField.displayName || foundField.name;
    },
    [fields]
  );

  const globalFiltersWithMetadata = React.useMemo(() => {
    if (!currentView?.globalFilters) {
      return [];
    }

    const facets = initialFacets || data?.facets;

    return currentView.globalFilters.map(filter => ({
      ...filter,
      facetValues: facets?.[filter.name],
      displayName: getGlobalFieldDisplayName(filter.name)
    }));
  }, [currentView?.globalFilters, data?.facets, getGlobalFieldDisplayName, initialFacets]);

  return (
    <div className="globalFilter">
      {globalFiltersWithMetadata
        ?.filter(filter => filter.type !== GEAR_FIELD_TYPE.DATE && filter.type !== GEAR_FIELD_TYPE.DATETIME)
        .map(filter => (
          <div key={filter.id} className={'filterItem'}>
            <GlobalFilter filterState={filter} />
          </div>
        ))}
      {globalFiltersWithMetadata?.some(
        filter => filter.type === GEAR_FIELD_TYPE.DATE || filter.type === GEAR_FIELD_TYPE.DATETIME
      ) && (
        <div key={'groupedDateGlobalFilter'} className={'filterItem'}>
          <GroupedDateGlobalFilter
            dateFiltersState={globalFiltersWithMetadata.filter(
              filter => filter.type === GEAR_FIELD_TYPE.DATE || filter.type === GEAR_FIELD_TYPE.DATETIME
            )}
          />
        </div>
      )}
    </div>
  );
};
